import { DatePicker, Form, Input, Modal, Select, Space } from "antd";
import axios from "axios";
import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import BASE_URL from "../constants";
import AuthContext from "../context/AuthContext";
import { ACTIVITY_TYPE_OPTIONS } from "../utils/activityTypes";

const CreateActivityAdminForm = ({
	open,
	onCreate,
	onCancel,
	adminUser,
	onAdminUserChange,
}) => {
	const { authTokens } = useContext(AuthContext);
	const [form] = Form.useForm();
	const { TextArea } = Input;
	const [disableTextArea, setDisableTextArea] = useState(false);
	// Issue #69: 送信中は OK を受け付けない。state だけで判定すると反映が次のレンダー
	// 以降になり、同じティックで続けて押された分を弾けないため、押された瞬間に同期で
	// 閉じる ref をガードにする（state はボタンの見た目専用）。
	const submittingRef = useRef(false);
	const [submitting, setSubmitting] = useState(false);

	const [users, setUsers] = useState([]);
	// Issue #88: 開いた直後に最初の入力欄（対象ユーザー）へフォーカスする。
	// forceRender で中身が開く前から mount されているため autoFocus 属性は効かない。
	const adminUserRef = useRef(null);
	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const handleUserChange = (value) => {
		onAdminUserChange(value); // Call the function passed from HomePage to handle adminUser change
	};

	const fetchUsers = useCallback(async () => {
		try {
			const response = await axios.get(`${BASE_URL}/user/get_users/`, {
				headers: {
					Authorization: "Bearer " + String(authTokens.access),
				},
			});
			setUsers(response.data);
		} catch (error) {
			console.error("Error fetching users:", error);
		}
	}, [authTokens]);

	useEffect(() => {
		fetchUsers();
	}, [fetchUsers]);

	useEffect(() => {
		// Set initial state based on the default value of activityType
		const initialValue = form.getFieldValue("activityType");
		setDisableTextArea(initialValue === 0 || initialValue === 2);
	}, [form]);

	const handleActivityTypeChange = (value) => {
		setDisableTextArea(value === 0 || value === 2);
	};

	useEffect(() => {
		if (!open) return undefined;
		const timer = setTimeout(() => adminUserRef.current?.focus(), 0);
		return () => clearTimeout(timer);
	}, [open]);

	return (
		<Modal
			forceRender
			open={open}
			// Issue #88: 幅 1020px 固定ではノート PC・画面分割など横幅の狭い環境で
			// モーダルが見切れる。画面幅に追従させ、広い画面では従来と同じ 1020px を上限にする。
			width="94vw"
			style={{ maxWidth: 1020 }}
			title="Add Activity For User"
			okText="Create"
			cancelText="Cancel"
			confirmLoading={submitting}
			cancelButtonProps={{ disabled: submitting }}
			closable={!submitting}
			maskClosable={!submitting}
			onCancel={() => {
				form.resetFields();
				setDisableTextArea(false);
				onCancel();
			}}
			onOk={() => {
				if (submittingRef.current) return;
				submittingRef.current = true;
				setSubmitting(true);
				form
					.validateFields()
					.then(async (values) => {
						// Issue #88: 代理登録の成功通知に「誰の記録を作ったか」を出せるよう、
						// 選択中ユーザーの表示名を呼び出し元へ渡す（id だけでは名前が分からない）。
						const selectedUser = users.find(
							(candidate) => candidate.id === values.adminUser,
						);
						// リセットの順序は従来のまま（送信前にフォームを空にする）
						form.resetFields();
						setDisableTextArea(false);
						await onCreate({
							...values,
							adminUsername: selectedUser?.username ?? null,
						});
					})
					.catch((info) => {
						console.log("Validate Failed:", info);
					})
					.finally(() => {
						submittingRef.current = false;
						setSubmitting(false);
					});
			}}
		>
			<Form
				form={form}
				layout="vertical"
				name="admin_activity_form"
				initialValues={{
					modifier: "public",
				}}
			>
				<Form.Item
					name="adminUser"
					label="Select User"
					rules={[{ required: true, message: "Please select a user." }]}
				>
					<Select
						ref={adminUserRef}
						showSearch
						placeholder="Select User"
						optionFilterProp="children"
						filterOption={filterOption}
						onChange={handleUserChange}
						options={users.map((user) => ({
							value: user.id,
							label: user.username,
						}))}
					/>
				</Form.Item>
				<Form.Item
					name="adminActivityDate"
					label="Choose activity date"
					rules={[{ required: true, message: "Please select a date." }]}
				>
					<Space direction="vertical">
						<DatePicker
							onChange={(date, dateString) =>
								form.setFieldsValue({ adminActivityDate: dateString })
							}
							format="YYYY-MM-DD"
						/>
					</Space>
				</Form.Item>
				<Form.Item
					name="adminActivityType"
					label="Activity Type"
					rules={[
						{ required: true, message: "Please select an activity type." },
					]}
				>
					<Select
						showSearch
						placeholder="Select activity type"
						optionFilterProp="children"
						filterOption={filterOption}
						onChange={handleActivityTypeChange}
						options={ACTIVITY_TYPE_OPTIONS}
					/>
				</Form.Item>

				<Form.Item name="adminActivity" label="Activity">
					<TextArea
						style={{
							height: 120,
							resize: "none",
						}}
						placeholder="Log today's activity here (optional)"
						disabled={disableTextArea}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default CreateActivityAdminForm;
